/**
 * Datetime range picker for the analytics toolbar — a quick-select sidebar +
 * month calendar with per-endpoint date (read-only, from the calendar) and time
 * (HH:mm) inputs. Built on the DS `Popover`, so positioning is collision-aware
 * (Radix/Floating-UI flips/shifts near the viewport edge — no manual overflow
 * math needed). Emits an ISO `TimeRange` on Apply. Native Date only (no lib).
 */
import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent, Button } from '@vnetwork/ui/primitives';
import styles from './WafDateTimeRangePicker.module.css';

export interface TimeRange {
  /** ISO 8601 with local offset, e.g. "2026-06-27T00:00:00+07:00". */
  from: string;
  to: string;
  /** Preset label (e.g. "Last 3 days") or "Custom". */
  label: string;
}

type PresetUnit = 'minute' | 'hour' | 'day';
interface Preset {
  value: string;
  label: string;
  amount: number;
  unit: PresetUnit;
}

export const PRESETS: Preset[] = [
  { value: '60m', label: 'Last 60 minutes', amount: 60, unit: 'minute' },
  { value: '6h', label: 'Last 6 hours', amount: 6, unit: 'hour' },
  { value: '12h', label: 'Last 12 hours', amount: 12, unit: 'hour' },
  { value: '24h', label: 'Last 24 hours', amount: 24, unit: 'hour' },
  { value: '3d', label: 'Last 3 days', amount: 3, unit: 'day' },
  { value: '7d', label: 'Last 7 days', amount: 7, unit: 'day' },
  { value: '14d', label: 'Last 14 days', amount: 14, unit: 'day' },
  { value: '30d', label: 'Last 30 days', amount: 30, unit: 'day' },
];

// ---- date helpers (all local-timezone) -------------------------------------

const pad = (n: number) => String(n).padStart(2, '0');
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const fmtDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtTime = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const fmtFooter = (d: Date) => `${fmtDay(d)} ${fmtTime(d)}`;

/** Combine a date-only Date + "HH:mm" + seconds into a full local Date. */
function combine(date: Date, hhmm: string, sec: number): Date {
  const [h, m] = hhmm.split(':').map(Number);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), h || 0, m || 0, sec, 0);
}

/** Local-offset ISO 8601, e.g. "2026-06-27T00:00:00+07:00". */
export function toISO(d: Date): string {
  const off = -d.getTimezoneOffset();
  const sign = off >= 0 ? '+' : '-';
  const abs = Math.abs(off);
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
    `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
  );
}

/**
 * Resolve a preset to a concrete `{ from, to }` against "now". Sub-day presets
 * roll back from the current instant; day presets are calendar-day aligned
 * (00:00:00 of the first day → 23:59:59 today), matching the vendor dashboard.
 */
export function presetRange(value: string): { from: Date; to: Date } {
  const p = PRESETS.find((x) => x.value === value) ?? PRESETS[5];
  const now = new Date();
  if (p.unit === 'day') {
    const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (p.amount - 1), 0, 0, 0);
    const to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    return { from, to };
  }
  const ms = p.unit === 'minute' ? p.amount * 60_000 : p.amount * 3_600_000;
  return { from: new Date(now.getTime() - ms), to: now };
}

/** 6×7 day grid (Monday-first) covering the month containing `view`. */
function monthGrid(view: Date): Date[] {
  const first = new Date(view.getFullYear(), view.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7; // Mon=0 … Sun=6
  const start = new Date(first.getFullYear(), first.getMonth(), 1 - offset);
  return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
}

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

// ---- component -------------------------------------------------------------

export function WafDateTimeRangePicker({
  value,
  onApply,
}: {
  value: TimeRange;
  onApply: (range: TimeRange) => void;
}) {
  const [open, setOpen] = useState(false);

  // Draft selection (calendar endpoints are date-only; times are separate).
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const [fromTime, setFromTime] = useState('00:00');
  const [toTime, setToTime] = useState('23:59');
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [view, setView] = useState(() => new Date());
  const [hover, setHover] = useState<Date | null>(null);

  // Seed the draft from the committed value each time the panel opens.
  useEffect(() => {
    if (!open) return;
    const from = new Date(value.from);
    const to = new Date(value.to);
    const validFrom = !Number.isNaN(from.getTime());
    const validTo = !Number.isNaN(to.getTime());
    setRangeStart(validFrom ? startOfDay(from) : null);
    setRangeEnd(validTo ? startOfDay(to) : null);
    setFromTime(validFrom ? fmtTime(from) : '00:00');
    setToTime(validTo ? fmtTime(to) : '23:59');
    setActivePreset(PRESETS.find((p) => p.label === value.label)?.value ?? null);
    setView(validTo ? to : new Date());
    setHover(null);
  }, [open, value.from, value.to, value.label]);

  const pickPreset = (p: Preset) => {
    const { from, to } = presetRange(p.value);
    setRangeStart(startOfDay(from));
    setRangeEnd(startOfDay(to));
    setFromTime(fmtTime(from));
    setToTime(fmtTime(to));
    setActivePreset(p.value);
    setView(to);
    setHover(null);
  };

  // Calendar click: first sets the start, second sets the end (restart if before).
  const pickDay = (d: Date) => {
    setActivePreset(null);
    if (!rangeStart || rangeEnd) {
      setRangeStart(d);
      setRangeEnd(null);
      return;
    }
    if (d < rangeStart) {
      setRangeStart(d);
      return;
    }
    setRangeEnd(d);
  };

  // The tentative end while hovering (only when a start is staged, no end yet).
  const previewEnd = rangeStart && !rangeEnd && hover && hover >= rangeStart ? hover : null;

  const cellState = (d: Date) => {
    const end = rangeEnd ?? previewEnd;
    const isStart = rangeStart && sameDay(d, rangeStart);
    const isEnd = end && sameDay(d, end);
    const inside = rangeStart && end && d > rangeStart && d < end;
    return {
      rangeStart: !!isStart,
      rangeEnd: !!isEnd,
      endpoint: !!isStart || !!isEnd,
      inRange: !!inside && !!rangeEnd,
      preview: !!inside && !rangeEnd,
    };
  };

  const fromDate = rangeStart ? combine(rangeStart, fromTime, 0) : null;
  const toDate = rangeEnd ? combine(rangeEnd, toTime, 59) : null;
  const valid =
    !!fromDate &&
    !!toDate &&
    /^\d{2}:\d{2}$/.test(fromTime) &&
    /^\d{2}:\d{2}$/.test(toTime) &&
    fromDate <= toDate;

  const apply = () => {
    if (!valid || !fromDate || !toDate) return;
    const label = activePreset ? PRESETS.find((p) => p.value === activePreset)!.label : 'Custom';
    onApply({ from: toISO(fromDate), to: toISO(toDate), label });
    setOpen(false);
  };

  const grid = useMemo(() => monthGrid(view), [view]);
  const today = new Date();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="secondary" startIcon={<CalendarDays size={15} />}>
          {value.label === 'Custom'
            ? `${fmtFooter(new Date(value.from))} → ${fmtFooter(new Date(value.to))}`
            : value.label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" collisionPadding={12} avoidCollisions style={{ padding: 0, maxWidth: 480 }}>
        <div className={styles.panel}>
          <div className={styles.sidebar}>
            {PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                className={`${styles.quick}${activePreset === p.value ? ` ${styles.active}` : ''}`}
                onClick={() => pickPreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className={styles.right}>
            <div className={styles.inputs}>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>From</span>
                <input
                  className={styles.dateInput}
                  type="text"
                  readOnly
                  value={rangeStart ? fmtDay(rangeStart) : ''}
                  placeholder="YYYY-MM-DD"
                  aria-label="From date"
                />
                <input
                  className={styles.timeInput}
                  type="time"
                  value={fromTime}
                  onChange={(e) => { setFromTime(e.target.value); setActivePreset(null); }}
                  aria-label="From time"
                />
              </div>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>To</span>
                <input
                  className={styles.dateInput}
                  type="text"
                  readOnly
                  value={rangeEnd ? fmtDay(rangeEnd) : ''}
                  placeholder="YYYY-MM-DD"
                  aria-label="To date"
                />
                <input
                  className={styles.timeInput}
                  type="time"
                  value={toTime}
                  onChange={(e) => { setToTime(e.target.value); setActivePreset(null); }}
                  aria-label="To time"
                />
              </div>
            </div>

            <div>
              <div className={styles.calHeader}>
                <button
                  type="button"
                  className={styles.navBtn}
                  onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
                  aria-label="Previous month"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className={styles.monthLabel}>
                  {view.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
                <button
                  type="button"
                  className={styles.navBtn}
                  onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
                  aria-label="Next month"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
              <div className={styles.weekHeader}>
                {WEEKDAYS.map((w) => <span key={w} className={styles.weekDay}>{w}</span>)}
              </div>
              <div className={styles.grid} onMouseLeave={() => setHover(null)}>
                {grid.map((d) => {
                  const st = cellState(d);
                  const otherMonth = d.getMonth() !== view.getMonth();
                  const cellCls = [
                    styles.cell,
                    st.inRange ? styles.inRange : '',
                    st.preview ? styles.preview : '',
                    st.rangeStart ? styles.rangeStart : '',
                    st.rangeEnd ? styles.rangeEnd : '',
                  ].filter(Boolean).join(' ');
                  const dayCls = [
                    styles.day,
                    otherMonth ? styles.other : '',
                    sameDay(d, today) ? styles.today : '',
                    st.endpoint ? styles.endpoint : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <div key={d.getTime()} className={cellCls}>
                      <button
                        type="button"
                        className={dayCls}
                        onClick={() => pickDay(d)}
                        onMouseEnter={() => setHover(d)}
                      >
                        {d.getDate()}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={styles.footer}>
              <div className={styles.footerActions}>
                <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
                <Button size="sm" onClick={apply} disabled={!valid}>Apply</Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default WafDateTimeRangePicker;
